import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBrain, faReply, faTrash, faDatabase, 
    faQuestionCircle, faMagic, faSync, faCheckCircle 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminBotManager = () => {
    const { t } = useTranslation();
    const [unanswered, setUnanswered] = useState([]);
    const [knowledge, setKnowledge] = useState([]);
    const [activeTab, setActiveTab] = useState('unanswered'); 
    const [newReply, setNewReply] = useState({});
    const [loading, setLoading] = useState(false);

    const getOptions = useCallback((method = 'GET', body = null) => {
        const token = localStorage.getItem('token');
        const options = {
            method: method,
            headers: { 
                'ngrok-skip-browser-warning': 'true', 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        };
        if (body) options.body = JSON.stringify(body);
        return options;
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === 'unanswered' 
                ? '/api/bot-system/admin/unanswered' 
                : '/api/bot-system/knowledge_base';
            
            const res = await fetch(`${API_BASE}${endpoint}`, getOptions());
            
            if (res.status === 401 || res.status === 403) {
                alert(t('access_denied_relogin'));
                return;
            }

            const data = await res.json();
            const safeData = Array.isArray(data) ? data : [];
            
            if (activeTab === 'unanswered') {
                setUnanswered(safeData);
            } else {
                setKnowledge(safeData);
            }
        } catch (e) {
            console.error("🔥 Fetch error:", e);
            setUnanswered([]);
            setKnowledge([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, getOptions, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleTrain = async (id, question) => {
        const answer = newReply[id];
        if (!answer) return alert(t('enter_answer_error'));
        try {
            const res = await fetch(`${API_BASE}/api/bot-system/admin/train-bot`, 
                getOptions('POST', { messageId: id, question, answer })
            );
            
            if (res.ok) {
                setNewReply(prev => {
                    const updated = {...prev};
                    delete updated[id];
                    return updated;
                });
                fetchData();
            } else {
                const errData = await res.json();
                alert(`${t('error')}: ${errData.message || t('train_failed')}`);
            }
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_knowledge_confirm'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/bot-system/knowledge_base/${id}`, 
                getOptions('DELETE')
            );
            if (res.ok) fetchData();
        } catch (e) { console.error(e); }
    };

    return (
        <div style={styles.dashboard}>
            {/* Analytics Cards */}
            <div style={styles.analyticsRow}>
                <AnalyticCard title={t('unanswered_count')} value={(unanswered || []).length} icon={faQuestionCircle} grad="linear-gradient(135deg, #FF5630 0%, #FF8B71 100%)" />
                <AnalyticCard title={t('knowledge_count')} value={(knowledge || []).length} icon={faBrain} grad="linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)" />
                <AnalyticCard title={t('bot_status')} value={t('status_active')} icon={faMagic} grad="linear-gradient(135deg, #01B574 0%, #2AF598 100%)" />
            </div>

            {/* Header with Tabs */}
            <div style={styles.mainHeader}>
                <div style={styles.tabGroup}>
                    <button style={activeTab === 'unanswered' ? styles.activeTab : styles.tab} onClick={() => setActiveTab('unanswered')}>{t('tab_training')}</button>
                    <button style={activeTab === 'database' ? styles.activeTab : styles.tab} onClick={() => setActiveTab('database')}>{t('tab_database')}</button>
                </div>
                <button onClick={fetchData} style={styles.refreshCircle} title={t('refresh')}>
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>
            </div>

            {/* Content Area */}
            <div style={styles.contentBody}>
                {activeTab === 'unanswered' ? (
                    <div style={styles.grid}>
                        {(unanswered || []).length === 0 && !loading && <EmptyState text={t('all_questions_answered')} />}
                        {(unanswered || []).map(q => (
                            <div key={q.id} style={styles.trainCard}>
                                <div style={styles.questionSection}>
                                    <div style={styles.questionBubble}>{q.question}</div>
                                    <textarea 
                                        style={styles.textarea}
                                        value={newReply[q.id] || ''}
                                        onChange={(e) => setNewReply({...newReply, [q.id]: e.target.value})}
                                        placeholder={t('write_answer_placeholder')}
                                    />
                                    <button onClick={() => handleTrain(q.id, q.question)} style={styles.trainBtn}>
                                        <FontAwesomeIcon icon={faReply} style={{marginRight: '8px'}} />
                                        {t('btn_train')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={styles.tableCard}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.thRow}>
                                    <th style={{...styles.th, width: '35%'}}>{t('table_question')}</th>
                                    <th style={{...styles.th, width: '50%'}}>{t('table_answer')}</th>
                                    <th style={{...styles.th, width: '15%', textAlign: 'center'}}>{t('table_action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(knowledge || []).map(k => (
                                    <tr key={k.id} style={styles.tr}>
                                        <td style={styles.tdQuestion}>{k.question}</td>
                                        <td style={styles.tdAnswer}>{k.answer}</td>
                                        <td style={styles.tdAction}>
                                            <button onClick={() => handleDelete(k.id)} style={styles.deleteBtn}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(knowledge || []).length === 0 && !loading && (
                            <div style={{padding: '40px', textAlign: 'center', color: '#A3AED0'}}>
                                {t('database_empty')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Sub-components
const AnalyticCard = ({ title, value, icon, grad }) => (
    <div style={{...styles.anaCard, background: grad}}>
        <div style={styles.anaIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div><p style={styles.anaLabel}>{title}</p><h3 style={styles.anaVal}>{value}</h3></div>
    </div>
);

const EmptyState = ({ text }) => (
    <div style={styles.emptyState}>
        <FontAwesomeIcon icon={faCheckCircle} style={{fontSize: '50px', color: '#01B574', marginBottom: '15px'}} />
        <p style={{fontSize: '18px', fontWeight: 'bold', color: '#2B3674'}}>{text}</p>
    </div>
);

const styles = {
    dashboard: { 
        padding: '20px', 
        backgroundColor: '#F4F7FE', 
        minHeight: '100vh', 
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box'
    },
    analyticsRow: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
    },
    anaCard: { 
        padding: '20px', 
        borderRadius: '20px', 
        color: '#fff', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '15px', 
        boxShadow: '0 10px 20px rgba(0,0,0,0.1)' 
    },
    anaIconBox: { 
        width: '45px', 
        height: '45px', 
        borderRadius: '12px', 
        background: 'rgba(255,255,255,0.2)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: '20px' 
    },
    anaLabel: { fontSize: '14px', margin: 0, opacity: 0.9 },
    anaVal: { fontSize: '24px', margin: 0, fontWeight: 'bold' },
    mainHeader: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '25px',
        flexWrap: 'wrap',
        gap: '15px'
    },
    tabGroup: { display: 'flex', background: '#E0E5F2', padding: '5px', borderRadius: '12px' },
    tab: { padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#707EAE', transition: '0.3s' },
    activeTab: { padding: '10px 20px', border: 'none', background: '#fff', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', color: '#4318FF', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' },
    refreshCircle: { border: 'none', background: '#fff', width: '45px', height: '45px', borderRadius: '50%', cursor: 'pointer', color: '#4318FF', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    contentBody: { width: '100%', overflow: 'hidden' },
    grid: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px' 
    },
    trainCard: { 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '25px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)' 
    },
    questionBubble: { 
        background: '#F4F7FE', 
        padding: '15px', 
        borderRadius: '15px', 
        marginBottom: '15px', 
        fontWeight: '600', 
        color: '#2B3674', 
        borderLeft: '5px solid #4318FF',
        wordBreak: 'break-word'
    },
    textarea: { 
        width: '100%', 
        padding: '15px', 
        borderRadius: '15px', 
        border: '1px solid #E0E5F2', 
        minHeight: '100px', 
        outline: 'none', 
        fontSize: '14px', 
        boxSizing: 'border-box'
    },
    trainBtn: { 
        width: '100%', 
        marginTop: '10px', 
        padding: '12px', 
        background: '#01B574', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '15px', 
        cursor: 'pointer', 
        fontWeight: 'bold' 
    },
    tableCard: { 
        background: '#fff', 
        borderRadius: '25px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        width: '100%',
        overflow: 'hidden' // Սա շատ կարևոր է
    },
    table: { 
        width: '100%', 
        borderCollapse: 'collapse',
        tableLayout: 'fixed' // Սա ստիպում է սյուներին մնալ իրենց տեղում
    },
    thRow: { background: '#F4F7FE' },
    th: { 
        padding: '15px', 
        textAlign: 'left', 
        color: '#A3AED0', 
        fontSize: '12px', 
        textTransform: 'uppercase' 
    },
    tr: { borderBottom: '1px solid #F4F7FE' },
    tdQuestion: { 
        padding: '15px', 
        fontWeight: 'bold', 
        color: '#2B3674', 
        wordBreak: 'break-word',
        verticalAlign: 'top',
        fontSize: '14px'
    },
    tdAnswer: { 
        padding: '15px', 
        color: '#475569', 
        fontSize: '13px', 
        wordBreak: 'break-word',
        verticalAlign: 'top'
    },
    tdAction: { 
        padding: '15px', 
        textAlign: 'center',
        verticalAlign: 'top'
    },
    deleteBtn: { 
        background: '#FEE2E2', 
        color: '#EF4444', 
        border: 'none', 
        padding: '8px', 
        borderRadius: '10px', 
        cursor: 'pointer' 
    },
    emptyState: { 
        textAlign: 'center', 
        padding: '50px', 
        background: '#fff', 
        borderRadius: '25px', 
        gridColumn: '1/-1' 
    }
};

export default AdminBotManager;